import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { useForm } from "react-hook-form";

import filters from "../../../../filters";
import utils from "../../../../utils";
import {
  FREQUENCIES,
  generateRecurrenceRuleString,
} from "../../../../Recurrence";

import type Account from "../../../../Account";
import type { components } from "./schema";
import type { ReactLocalization } from "@fluent/react";
import type { Dollars } from "../../../../shared";
import type { DefaultValues } from "react-hook-form";

export type InternationalWireForm = components["schemas"]["InternationalWire"];

export const getInternationalWireRecurringRule = (
  values: InternationalWireForm,
): string => {
  const { startDate, recurringRule } = values;
  if (!startDate) return "";

  const formattedStartDate = startDate.includes("-")
    ? startDate
    : DateTime.fromFormat(startDate, "M/d/yyyy").toFormat("yyyy-MM-dd");
  const formattedTodayDate = DateTime.now().toFormat("yyyy-MM-dd");

  let frequency = "";
  if (recurringRule?.isVisible) {
    frequency = recurringRule.frequency || "";
  } else if (formattedStartDate > formattedTodayDate) {
    frequency = FREQUENCIES.ONCE;
  }
  let untilDate;
  if (recurringRule?.ends === "on" && recurringRule.untilDate) {
    untilDate = recurringRule.untilDate.includes("-")
      ? recurringRule.untilDate
      : DateTime.fromFormat(recurringRule.untilDate, "M/d/yyyy").toFormat(
          "yyyy-MM-dd",
        );
  }
  const count =
    recurringRule?.ends === "after" ? recurringRule.count : undefined;

  return generateRecurrenceRuleString(
    frequency,
    formattedStartDate,
    untilDate,
    count,
  );
};

const getRoundedConversionRate = (
  baseAmount: string | null,
  settlementAmount: string,
) => {
  if (!baseAmount || !settlementAmount) return "";
  return Number(
    parseFloat(settlementAmount.replace(/[^0-9.-]+/g, "")) /
      parseFloat(baseAmount.replace(/[^0-9.-]+/g, "")),
  ).toPrecision(4);
};

export const getForeXDetails = (swiftWire: {
  amount: number;
  quote?: API.FetchExchangeRateQuote.Response;
}): {
  settlementAmount: string;
  settlementCurrency: string;
  settlementAmountIsEstimated: boolean;
  baseAmount: string | null;
  baseCurrency: string | null;
  baseAmountIsEstimated: boolean | null;
  roundedConversionRate: string;
} => {
  // if quote but no contract
  if (swiftWire.quote) {
    if (
      swiftWire.quote.specified_currency === swiftWire.quote.settlement_currency
    ) {
      // if specified currency is settlement
      const settlementAmount = utils.currencyFormattedValue(
        swiftWire.quote.amount,
        swiftWire.quote.specified_currency,
      );
      const baseAmount = swiftWire.quote.converted_amount
        ? utils.currencyFormattedValue(
            swiftWire.quote.converted_amount,
            swiftWire.quote.converted_currency,
          )
        : null;
      return {
        settlementAmount,
        settlementCurrency: swiftWire.quote.specified_currency,
        settlementAmountIsEstimated: false,
        baseAmount,
        baseCurrency: swiftWire.quote.converted_currency || null,
        baseAmountIsEstimated: true,
        roundedConversionRate: getRoundedConversionRate(
          baseAmount,
          settlementAmount,
        ),
      };
    }
    // if specified currency is base USD
    const settlementAmount = swiftWire.quote.converted_amount
      ? utils.currencyFormattedValue(
          swiftWire.quote.converted_amount,
          swiftWire.quote.converted_currency,
        )
      : utils.currencyFormattedValue(
          swiftWire.quote.amount,
          swiftWire.quote.specified_currency,
        );
    const baseAmount = utils.currencyFormattedValue(
      swiftWire.quote.amount,
      swiftWire.quote.specified_currency,
    );
    return {
      settlementAmount,
      settlementCurrency: swiftWire.quote.converted_currency,
      settlementAmountIsEstimated: true,
      baseAmount,
      baseCurrency: swiftWire.quote.specified_currency,
      baseAmountIsEstimated: false,
      roundedConversionRate: getRoundedConversionRate(
        baseAmount,
        settlementAmount,
      ),
    };
  }
  // if no quote or contract
  return {
    settlementAmount: filters.currency(swiftWire.amount as Dollars, {
      hasDecimal: true,
    }),
    settlementCurrency: "USD",
    settlementAmountIsEstimated: false,
    baseAmount: null,
    baseCurrency: null,
    baseAmountIsEstimated: null,
    roundedConversionRate: "",
  };
};

export const validateAmountAgainstBalance = (
  data: {
    fromAccountId?: string;
    amount?: number | string;
    recipientCurrency?: string;
    currencyTransferPreference?: "receive" | "send";
    quote?: {
      converted_amount?: string;
    };
  },
  accounts: Account[],
  l10n?: ReactLocalization,
): { isValid: boolean; error?: string } => {
  if (!data.fromAccountId || !data.amount) {
    return { isValid: true };
  }

  const selectedAccount = accounts.find((acc) => acc.id === data.fromAccountId);

  if (!selectedAccount) {
    return { isValid: true };
  }

  const balance = selectedAccount.transferableBalanceAsFloat();
  const amountValue =
    typeof data.amount === "string" ? parseFloat(data.amount) : data.amount;
  const error =
    l10n?.getString("available-balance-error") ||
    "Transfer amount cannot exceed source account's available balance.";
  // For USD wires or FX send preference, check the amount directly
  if (
    !data.recipientCurrency ||
    data.recipientCurrency === "USD" ||
    (data.quote && data.currencyTransferPreference === "send")
  ) {
    if (amountValue > balance) {
      return {
        isValid: false,
        error,
      };
    }
  }

  // For FX receive preference, check converted amount
  if (
    !data.quote &&
    data.currencyTransferPreference === "receive" &&
    data.recipientCurrency &&
    data.recipientCurrency !== "USD"
  ) {
    return {
      isValid: false,
      error: l10n?.getString("failed-to-fetch-quote-error"),
    };
  }
  if (
    data.quote?.converted_amount &&
    data.currencyTransferPreference === "receive"
  ) {
    const convertedAmount = parseFloat(data.quote.converted_amount);
    if (!Number.isNaN(convertedAmount) && convertedAmount > balance) {
      return {
        isValid: false,
        error,
      };
    }
  }

  return { isValid: true };
};

function createSchema(accounts: Account[] = [], l10n?: ReactLocalization) {
  return z
    .object({
      hasFormBeenStarted: z.boolean().default(false),
      fromAccountId: z.string().min(1, { message: "Required" }),
      recipientId: z.string().min(1, { message: "Required" }),
      amount: z.preprocess(
        (val) => {
          if (typeof val === "string" || typeof val === "number") {
            const num = Number(val);
            return Number.isNaN(num) ? undefined : num;
          }
          return val;
        },
        z.number({ required_error: "Required" }),
      ),
      wireReason: z.string(),
      wireReasonDescription: z.string(),
      memo: z.string().max(128, { message: "Maximum 128 characters" }),
      note: z.string(),
      recipientCurrency: z.string(),
      currencyTransferPreference: z.enum(["receive", "send"]),
      quote: z
        .object({
          id: z.string(),
          amount: z.string(),
          specified_currency: z.string(),
          converted_amount: z.string().optional(),
          converted_currency: z.string(),
          settlement_currency: z.string(),
          expiration_in_seconds: z.number().optional(),
          rate: z.string().optional(),
        })
        .optional(),
      swiftWireTemplateId: z.string().optional(),
      swiftWireTemplate: z.custom<API.SwiftWireTemplate>().optional(),
      startDate: z.string().optional(),
      recurringRule: z
        .object({
          isVisible: z.boolean(),
          ends: z.enum(["", "never", "on", "after"]),
          frequency: z.string().optional(),
          untilDate: z.string().optional(),
          count: z.string().optional(),
        })
        .optional(),
    })
    .superRefine((data, ctx) => {
      const template = data.swiftWireTemplate;

      // amount: required when template doesn't provide one, and must be > 0 when present
      if (!template?.amount) {
        if (!data.amount || data.amount <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: data.amount ? "Must be greater than 0" : "Required",
            path: ["amount"],
          });
        }
      } else if (data.amount && data.amount <= 0) {
        // Template provides amount, but user entered an invalid override
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be greater than 0",
          path: ["amount"],
        });
      }

      // wireReason is required when the template does not provide one
      if (!template?.wire_reason && !data.wireReason) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["wireReason"],
        });
      }

      if (data.recurringRule?.isVisible && !data.recurringRule.frequency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["recurringRule", "frequency"],
        });
      }
      if (data.recurringRule?.ends === "on" && !data.recurringRule.untilDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["recurringRule", "untilDate"],
        });
      }
      if (
        data.recurringRule?.ends === "after" &&
        (!data.recurringRule.count ||
          !Number.isInteger(Number(data.recurringRule.count)) ||
          Number(data.recurringRule.count) <= 0 ||
          Number(data.recurringRule.count) > 99)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Must be between 1 and 99",
          path: ["recurringRule", "count"],
        });
      }
    })
    .superRefine((data, ctx) => {
      const result = validateAmountAgainstBalance(data, accounts, l10n);
      if (!result.isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result.error,
          path: ["amount"],
        });
      }
    });
}

export function createDefaultValues(): DefaultValues<InternationalWireForm> {
  return {
    hasFormBeenStarted: false,
    fromAccountId: "",
    recipientId: "",
    amount: 0,
    wireReason: "",
    wireReasonDescription: "",
    memo: "",
    note: "",
    recipientCurrency: "",
    currencyTransferPreference: "send" as const,
    quote: undefined,
    swiftWireTemplateId: undefined,
    swiftWireTemplate: undefined,
    startDate: DateTime.now().toFormat("MM/dd/yyyy"),
    recurringRule: {
      isVisible: false,
      ends: "",
      frequency: FREQUENCIES.ONCE,
      untilDate: "",
      count: "",
    },
  };
}

export const useInternationalWireForm = (
  accounts: Account[] = [],
  l10n?: ReactLocalization,
) => {
  const schema = createSchema(accounts, l10n);
  return useForm<InternationalWireForm>({
    defaultValues: createDefaultValues(),
    resolver: zodResolver(schema),
    mode: "onChange",
  });
};
